import { useMemo, useRef, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";

import { useCalendar } from "../api/hooks/useCalendar";
import type { AnalyticsFilters } from "../api/hooks/useSummary";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { useGlobalFilters } from "../lib/globalFilters";
import { monthBounds, yearBounds } from "../lib/journal";
import { formatMoney, sumDecimals } from "../lib/money";

const WEEKDAY_HEADERS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"] as const;
const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;
// Title-case weekday headers for the compact annual mini-grids (the big grid
// uses the uppercase WEEKDAY_HEADERS above).
const WEEKDAY_MINI_HEADERS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
const WEEK_ROWS = 6;
const DAYS_PER_WEEK = 7;

interface MonthCursor {
  year: number;
  month: number;
}

function monthCursorFromNow(now: Date): MonthCursor {
  return { year: now.getUTCFullYear(), month: now.getUTCMonth() };
}

function shiftMonth(cursor: MonthCursor, delta: number): MonthCursor {
  const total = cursor.year * 12 + cursor.month + delta;
  return { year: Math.floor(total / 12), month: ((total % 12) + 12) % 12 };
}

function monthLabel(cursor: MonthCursor): string {
  return `${MONTH_LABELS[cursor.month]} ${cursor.year}`;
}

function pad2(n: number): string {
  return n.toString().padStart(2, "0");
}

function todayIso(now: Date): string {
  return `${now.getUTCFullYear().toString().padStart(4, "0")}-${pad2(now.getUTCMonth() + 1)}-${pad2(now.getUTCDate())}`;
}

function intersectBounds(
  localSince: string,
  localUntil: string,
  globalSince: string | undefined,
  globalUntil: string | undefined,
): { since: string; until: string } {
  let since = localSince;
  let until = localUntil;
  if (globalSince !== undefined) {
    const dt = `${globalSince}T00:00:00.000Z`;
    if (dt > since) since = dt;
  }
  if (globalUntil !== undefined) {
    const dt = `${globalUntil}T23:59:59.999Z`;
    if (dt < until) until = dt;
  }
  return { since, until };
}

interface CalendarCell {
  isoDate: string | null;
  day: number | null;
}

function buildMonthGrid(cursor: MonthCursor): CalendarCell[][] {
  const firstDayOfWeek = new Date(Date.UTC(cursor.year, cursor.month, 1)).getUTCDay();
  const daysInMonth = new Date(Date.UTC(cursor.year, cursor.month + 1, 0)).getUTCDate();

  const rows: CalendarCell[][] = [];
  let cursorDay = 1 - firstDayOfWeek;
  for (let w = 0; w < WEEK_ROWS; w += 1) {
    const row: CalendarCell[] = [];
    for (let d = 0; d < DAYS_PER_WEEK; d += 1) {
      if (cursorDay >= 1 && cursorDay <= daysInMonth) {
        const iso = `${cursor.year.toString().padStart(4, "0")}-${pad2(cursor.month + 1)}-${pad2(cursorDay)}`;
        row.push({ isoDate: iso, day: cursorDay });
      } else {
        row.push({ isoDate: null, day: null });
      }
      cursorDay += 1;
    }
    rows.push(row);
  }
  return rows;
}

function parseIsoDate(iso: string): [number, number, number] {
  const parts = iso.split("-");
  return [Number(parts[0]), Number(parts[1]), Number(parts[2])];
}

function rowSundayIso(row: CalendarCell[]): string | null {
  const firstInMonth = row.find((c) => c.isoDate !== null);
  if (firstInMonth === undefined || firstInMonth.isoDate === null) return null;
  const idx = row.indexOf(firstInMonth);
  const [y, m, d] = parseIsoDate(firstInMonth.isoDate);
  const sunday = new Date(Date.UTC(y, m - 1, d - idx));
  return `${sunday.getUTCFullYear().toString().padStart(4, "0")}-${pad2(sunday.getUTCMonth() + 1)}-${pad2(sunday.getUTCDate())}`;
}

// Which P&L the calendar surfaces: net (after commissions) or gross (before).
// The user picks this from the page header; it drives every tinted value below.
export type PnlMetric = "net" | "gross";

interface DayBucket {
  trade_count: number;
  net_pnl_amount: string;
  gross_pnl_amount: string;
}

interface WeekBucket {
  week_trade_count: number;
  week_net_pnl_amount: string;
  week_gross_pnl_amount: string;
}

// The bucket's amount for the active metric. Gross falls back to net when the
// series predates the gross field (defensive — the live API always sends both).
function dayAmount(bucket: DayBucket, metric: PnlMetric): string {
  return metric === "gross" ? bucket.gross_pnl_amount : bucket.net_pnl_amount;
}

function weekAmount(bucket: WeekBucket, metric: PnlMetric): string {
  return metric === "gross" ? bucket.week_gross_pnl_amount : bucket.week_net_pnl_amount;
}

function indexByDate(
  points: ReadonlyArray<{
    trade_date: string;
    trade_count: number;
    net_pnl_amount: string;
    gross_pnl_amount?: string;
  }>,
): Map<string, DayBucket> {
  const map = new Map<string, DayBucket>();
  for (const p of points) {
    map.set(p.trade_date, {
      trade_count: p.trade_count,
      net_pnl_amount: p.net_pnl_amount,
      gross_pnl_amount: p.gross_pnl_amount ?? p.net_pnl_amount,
    });
  }
  return map;
}

function indexByWeek(
  weeks: ReadonlyArray<{
    week_start: string;
    week_trade_count: number;
    week_net_pnl_amount: string;
    week_gross_pnl_amount?: string;
  }>,
): Map<string, WeekBucket> {
  const map = new Map<string, WeekBucket>();
  for (const w of weeks) {
    map.set(w.week_start, {
      week_trade_count: w.week_trade_count,
      week_net_pnl_amount: w.week_net_pnl_amount,
      week_gross_pnl_amount: w.week_gross_pnl_amount ?? w.week_net_pnl_amount,
    });
  }
  return map;
}

interface FullCell {
  isoDate: string;
  day: number;
  inMonth: boolean;
}

const MS_PER_DAY = 86_400_000;

// Like buildMonthGrid, but every one of the 42 cells carries a real date: the
// leading/trailing cells hold the adjacent month's days (rendered faded in the
// mini heatmap) so each annual mini-grid is a full, gapless 6x7 calendar that
// matches the reference layout.
function buildFullMonthGrid(year: number, month: number): FullCell[] {
  const firstDayOfWeek = new Date(Date.UTC(year, month, 1)).getUTCDay();
  const start = Date.UTC(year, month, 1 - firstDayOfWeek);
  return Array.from({ length: WEEK_ROWS * DAYS_PER_WEEK }, (_, i) => {
    const d = new Date(start + i * MS_PER_DAY);
    return {
      isoDate: `${d.getUTCFullYear().toString().padStart(4, "0")}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`,
      day: d.getUTCDate(),
      inMonth: d.getUTCMonth() === month,
    };
  });
}

export interface MonthTotal {
  tradeCount: number;
  netPnl: string;
  grossPnl: string;
}

// Sum net + gross P&L and trade count for a single month from the calendar day
// points (issue 13: the month view previously showed only per-week totals).
// Points outside the target year/month are ignored so the figure stays exact
// even if the API returns a wider window after intersecting global date filters.
// ``gross_pnl_amount`` falls back to net for series predating the gross field.
export function monthTotal(
  points: ReadonlyArray<{
    trade_date: string;
    trade_count: number;
    net_pnl_amount: string;
    gross_pnl_amount?: string;
  }>,
  year: number,
  month: number,
): MonthTotal {
  let count = 0;
  const nets: string[] = [];
  const grosses: string[] = [];
  for (const p of points) {
    const [y, m] = parseIsoDate(p.trade_date);
    if (y !== year || m - 1 !== month) continue;
    count += p.trade_count;
    nets.push(p.net_pnl_amount);
    grosses.push(p.gross_pnl_amount ?? p.net_pnl_amount);
  }
  return { tradeCount: count, netPnl: sumDecimals(nets), grossPnl: sumDecimals(grosses) };
}

// Sum net + gross P&L and trade count across the whole calendar year. Fed by a
// year-bounded query so the header can show an exact full-year figure.
export function yearTotal(
  points: ReadonlyArray<{
    trade_date: string;
    trade_count: number;
    net_pnl_amount: string;
    gross_pnl_amount?: string;
  }>,
  year: number,
): MonthTotal {
  let count = 0;
  const nets: string[] = [];
  const grosses: string[] = [];
  for (const p of points) {
    const [y] = parseIsoDate(p.trade_date);
    if (y !== year) continue;
    count += p.trade_count;
    nets.push(p.net_pnl_amount);
    grosses.push(p.gross_pnl_amount ?? p.net_pnl_amount);
  }
  return { tradeCount: count, netPnl: sumDecimals(nets), grossPnl: sumDecimals(grosses) };
}

// Sign-based emphasis for a P&L figure in the calendar header totals.
function amountToneClass(netPnl: string): string {
  const amount = Number(netPnl);
  if (amount > 0) return "font-semibold text-success";
  if (amount < 0) return "font-semibold text-danger";
  return "font-semibold text-on-surface";
}

interface DayCellProps {
  cell: CalendarCell;
  bucket: DayBucket | undefined;
  currency: string;
  metric: PnlMetric;
  isToday: boolean;
  onSelect: (isoDate: string) => void;
}

function DayCell({ cell, bucket, currency, metric, isToday, onSelect }: DayCellProps) {
  if (cell.isoDate === null) {
    // Off-month: empty cell, only the grid spacing shows.
    return <td className="p-0" aria-hidden="true" />;
  }

  const isoDate = cell.isoDate;
  const todayOutline = isToday
    ? " outline outline-2 -outline-offset-2 outline-primary"
    : "";

  // Day number sits top-right; the day's net P&L + trade count fill the bottom
  // (matches the dashboard calendar's rounded-card style).
  let tintClass = "border-outline-variant bg-surface-container-high";
  let body = (
    <span className="self-end text-xs font-semibold text-on-surface-variant">
      {cell.day}
    </span>
  );
  if (bucket !== undefined) {
    const value = dayAmount(bucket, metric);
    const amount = Number(value);
    let amountClass: string;
    if (amount > 0) {
      tintClass = "border-success bg-success-soft";
      amountClass = "text-success";
    } else if (amount < 0) {
      tintClass = "border-danger bg-danger-soft";
      amountClass = "text-danger";
    } else {
      tintClass = "border-outline-variant bg-surface-container-high";
      amountClass = "text-on-surface-variant";
    }
    const formattedAmount = formatMoney(value, { currency });
    const tradeLabel =
      bucket.trade_count === 1 ? "1 trade" : `${bucket.trade_count} trades`;
    body = (
      <>
        <span className="self-end text-xs font-semibold text-on-surface-variant">
          {cell.day}
        </span>
        <div className="mt-auto">
          <div className={`text-sm font-semibold ${amountClass}`}>
            {formattedAmount}
          </div>
          <div className="text-[10px] uppercase tracking-wide text-on-surface-variant">
            {tradeLabel}
          </div>
        </div>
      </>
    );
  }

  // Issue 14: clicking a day opens the Trades page filtered to that date -- but
  // only days that actually have trades are interactive; an empty day would open
  // an empty Trades view, so it renders as a plain, non-clickable cell.
  return (
    <td
      className={`h-20 overflow-hidden rounded-lg border align-top ${tintClass}${todayOutline}`}
      data-testid={`calendar-cell-${isoDate}`}
    >
      {bucket !== undefined ? (
        <button
          type="button"
          onClick={() => onSelect(isoDate)}
          aria-label={`View trades for ${isoDate}`}
          className="flex h-full w-full flex-col items-stretch px-2 py-1.5 text-left transition-colors hover:brightness-95"
        >
          {body}
        </button>
      ) : (
        <div className="flex h-full w-full flex-col items-stretch px-2 py-1.5">{body}</div>
      )}
    </td>
  );
}

interface WeekTotalCellProps {
  bucket: WeekBucket | undefined;
  currency: string;
  metric: PnlMetric;
  weekStartIso: string | null;
}

function WeekTotalCell({ bucket, currency, metric, weekStartIso }: WeekTotalCellProps) {
  if (weekStartIso === null) {
    return <td className="p-0" aria-hidden="true" />;
  }
  if (bucket === undefined) {
    return (
      <td
        className="h-20 overflow-hidden rounded-lg border border-outline-variant bg-surface-container-high align-top text-on-surface-variant"
        data-testid={`calendar-week-total-${weekStartIso}`}
      >
        <div className="flex h-full w-full flex-col px-2 py-1.5">
          <span className="text-[10px] uppercase tracking-wide text-on-surface-muted">
            Total
          </span>
        </div>
      </td>
    );
  }
  const value = weekAmount(bucket, metric);
  const amount = Number(value);
  let tintClass: string;
  let amountClass: string;
  if (amount > 0) {
    tintClass = "border-success bg-success-soft";
    amountClass = "text-success";
  } else if (amount < 0) {
    tintClass = "border-danger bg-danger-soft";
    amountClass = "text-danger";
  } else {
    tintClass = "border-outline-variant bg-surface-container-high";
    amountClass = "text-on-surface-variant";
  }
  const formattedAmount = formatMoney(value, { currency });
  const tradeLabel =
    bucket.week_trade_count === 1 ? "1 trade" : `${bucket.week_trade_count} trades`;
  return (
    <td
      className={`h-20 overflow-hidden rounded-lg border align-top ${tintClass}`}
      data-testid={`calendar-week-total-${weekStartIso}`}
    >
      <div className="flex h-full w-full flex-col px-2 py-1.5 text-right">
        <span className="text-[10px] uppercase tracking-wide text-on-surface-variant">
          Total
        </span>
        <div className="mt-auto">
          <div className={`text-sm font-semibold ${amountClass}`}>{formattedAmount}</div>
          <div className="text-[10px] uppercase tracking-wide text-on-surface-variant">
            {tradeLabel}
          </div>
        </div>
      </div>
    </td>
  );
}

// The shared big month grid -- a 6x7 day table with the weekly-total right rail.
// Reused by both the month view and the year view's drill-down grid so the two
// stay pixel-identical. The caller supplies the prebuilt grid plus the day/week
// buckets for the visible month.
interface MonthGridTableProps {
  grid: CalendarCell[][];
  dayBuckets: Map<string, DayBucket>;
  weekBuckets: Map<string, WeekBucket>;
  currency: string;
  metric: PnlMetric;
  today: string;
  onSelectDay: (isoDate: string) => void;
}

function MonthGridTable({
  grid,
  dayBuckets,
  weekBuckets,
  currency,
  metric,
  today,
  onSelectDay,
}: MonthGridTableProps) {
  return (
    <table
      className="w-full table-fixed border-separate border-spacing-1.5 text-sm"
      aria-label="Monthly P&L calendar"
    >
      <thead>
        <tr>
          {WEEKDAY_HEADERS.map((label) => (
            <th
              key={label}
              scope="col"
              className="w-[12.5%] pb-1 text-xs font-semibold uppercase tracking-wide text-on-surface-muted"
            >
              {label}
            </th>
          ))}
          <th
            scope="col"
            className="w-[12.5%] pb-1 text-xs font-semibold uppercase tracking-wide text-on-surface-muted"
          >
            Total
          </th>
        </tr>
      </thead>
      <tbody>
        {grid.map((row, ri) => {
          const weekStartIso = rowSundayIso(row);
          return (
            <tr key={ri}>
              {row.map((cell, ci) => (
                <DayCell
                  key={ci}
                  cell={cell}
                  bucket={cell.isoDate ? dayBuckets.get(cell.isoDate) : undefined}
                  currency={currency}
                  metric={metric}
                  isToday={cell.isoDate === today}
                  onSelect={onSelectDay}
                />
              ))}
              <WeekTotalCell
                bucket={weekStartIso ? weekBuckets.get(weekStartIso) : undefined}
                currency={currency}
                metric={metric}
                weekStartIso={weekStartIso}
              />
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

interface MiniMonthGridProps {
  monthIndex: number;
  year: number;
  dayBuckets: Map<string, DayBucket>;
  metric: PnlMetric;
  today: string;
  isSelected: boolean;
  onSelect: (month: number) => void;
}

// Per-cell appearance in the mini-grid: today gets a filled primary disc,
// adjacent-month spillover days are faded, in-month days are a rounded square
// tinted by net P&L sign (neutral when there is no trade data that day).
function miniCellClass(
  isToday: boolean,
  inMonth: boolean,
  bucket: DayBucket | undefined,
  metric: PnlMetric,
): string {
  if (isToday) return "rounded-full bg-primary font-semibold text-on-primary";
  if (!inMonth) return "text-on-surface-muted/50";
  if (bucket === undefined) return "rounded-[2px] bg-surface-container-high text-on-surface-variant";
  const amount = Number(dayAmount(bucket, metric));
  if (amount > 0) return "rounded-[2px] border border-success bg-success-soft text-on-surface";
  if (amount < 0) return "rounded-[2px] border border-danger bg-danger-soft text-on-surface";
  return "rounded-[2px] bg-surface text-on-surface-variant";
}

// One small month in the annual heatmap: a centered month name, a Sun-Sat
// header, and a full 6x7 grid of numbered day cells tinted by net P&L (matching
// the reference layout). The whole tile is a single button -- clicking it drills
// the big grid below to this month; the day itself is opened from the big grid.
function MiniMonthGrid({
  monthIndex,
  year,
  dayBuckets,
  metric,
  today,
  isSelected,
  onSelect,
}: MiniMonthGridProps) {
  const cells = buildFullMonthGrid(year, monthIndex);
  const selectedRing = isSelected ? " ring-2 ring-primary ring-inset" : "";
  return (
    <button
      type="button"
      onClick={() => onSelect(monthIndex)}
      aria-label={`${MONTH_LABELS[monthIndex]} ${year} — show in calendar`}
      data-testid={`calendar-mini-${year}-${pad2(monthIndex + 1)}`}
      className={`flex flex-col gap-1 rounded-md border border-outline-variant bg-surface-container p-2 hover:bg-surface-container-high${selectedRing}`}
    >
      <div className="text-center text-xs font-semibold text-on-surface">
        {MONTH_LABELS[monthIndex]}
      </div>
      <div className="grid grid-cols-7 gap-px" aria-hidden="true">
        {WEEKDAY_MINI_HEADERS.map((label, i) => (
          <span
            key={`h-${i}`}
            className="pb-0.5 text-center text-[8px] font-medium uppercase text-on-surface-muted"
          >
            {label}
          </span>
        ))}
        {cells.map((cell) => {
          // Highlight today only in its own month, not where it appears as an
          // adjacent month's faded spillover cell.
          const isToday = cell.inMonth && cell.isoDate === today;
          const bucket = cell.inMonth ? dayBuckets.get(cell.isoDate) : undefined;
          return (
            <span
              key={cell.isoDate}
              data-testid={cell.inMonth ? `calendar-mini-cell-${cell.isoDate}` : undefined}
              className={`flex aspect-square items-center justify-center text-[9px] leading-none ${miniCellClass(isToday, cell.inMonth, bucket, metric)}`}
            >
              {cell.day}
            </span>
          );
        })}
      </div>
    </button>
  );
}

interface CalendarSearch {
  y?: number;
  m?: number;
}

export function CalendarPage() {
  // The big calendar's month can be seeded from the URL so returning from the
  // Trades page (after a day-click) restores the same month.
  const search = useSearch({ strict: false }) as CalendarSearch;
  const [monthCursor, setMonthCursor] = useState<MonthCursor>(() =>
    search.y !== undefined && search.m !== undefined
      ? { year: search.y, month: search.m }
      : monthCursorFromNow(new Date()),
  );
  // The annual overview's year navigates independently of the big calendar.
  const [miniYear, setMiniYear] = useState<number>(() => monthCursor.year);
  // Net vs gross P&L for every tinted figure (day cells, week + month totals,
  // mini grids). The annual frame shows both regardless; this only flips which
  // metric the calendars themselves render.
  const [metric, setMetric] = useState<PnlMetric>("net");
  const global = useGlobalFilters();
  const navigate = useNavigate();

  // Big-calendar query: the visible month's bounds intersected with the global
  // date filter.
  const monthEffective = useMemo(() => {
    const mb = monthBounds(monthCursor);
    return intersectBounds(mb.since, mb.until, global.since, global.until);
  }, [monthCursor, global.since, global.until]);
  const monthFilters = useMemo<AnalyticsFilters>(
    () => ({
      since: monthEffective.since,
      until: monthEffective.until,
      account_id: global.account_id,
      tag_id: global.tag_id,
    }),
    [monthEffective, global.account_id, global.tag_id],
  );

  // Annual-overview query: a full year of day points for the 12 mini grids.
  const miniYearEffective = useMemo(() => {
    const yb = yearBounds({ year: miniYear, month: 0 });
    return intersectBounds(yb.since, yb.until, global.since, global.until);
  }, [miniYear, global.since, global.until]);
  const miniYearFilters = useMemo<AnalyticsFilters>(
    () => ({
      since: miniYearEffective.since,
      until: miniYearEffective.until,
      account_id: global.account_id,
      tag_id: global.tag_id,
    }),
    [miniYearEffective, global.account_id, global.tag_id],
  );

  // A filter window that sits entirely outside the queried month/year inverts
  // the intersected [since, until] range, which the API rejects. Disable those
  // queries so the calendar renders empty (no in-range data) instead of showing
  // "Failed to load calendar." -- the filter narrows data, never breaks display.
  const monthQuery = useCalendar(monthFilters, {
    enabled: monthEffective.since <= monthEffective.until,
  });
  const miniQuery = useCalendar(miniYearFilters, {
    enabled: miniYearEffective.since <= miniYearEffective.until,
  });

  const grid = useMemo(() => buildMonthGrid(monthCursor), [monthCursor]);
  const dayBuckets = useMemo(
    () => indexByDate(monthQuery.data?.points ?? []),
    [monthQuery.data],
  );
  const weekBuckets = useMemo(
    () => indexByWeek(monthQuery.data?.weeks ?? []),
    [monthQuery.data],
  );
  const miniDayBuckets = useMemo(
    () => indexByDate(miniQuery.data?.points ?? []),
    [miniQuery.data],
  );
  const monthSummary = useMemo(
    () => monthTotal(monthQuery.data?.points ?? [], monthCursor.year, monthCursor.month),
    [monthQuery.data, monthCursor.year, monthCursor.month],
  );
  // Year total for the annual frame's header (gross + net), scoped to the mini
  // overview's independently-navigated year.
  const miniYearSummary = useMemo(
    () => yearTotal(miniQuery.data?.points ?? [], miniYear),
    [miniQuery.data, miniYear],
  );
  const currency = monthQuery.data?.currency ?? miniQuery.data?.currency ?? "USD";
  const today = useMemo(() => todayIso(new Date()), []);
  const bigCalRef = useRef<HTMLDivElement>(null);

  function handleMonthBack() {
    setMonthCursor((c) => shiftMonth(c, -1));
  }
  function handleMonthForward() {
    setMonthCursor((c) => shiftMonth(c, 1));
  }
  function handleToday() {
    setMonthCursor(monthCursorFromNow(new Date()));
  }
  function handleYearBack() {
    setMiniYear((y) => y - 1);
  }
  function handleYearForward() {
    setMiniYear((y) => y + 1);
  }
  // Clicking a mini month drills the big calendar to that month (in the mini
  // overview's year) and scrolls it into view; the day itself is opened from
  // the big calendar via handleSelectDay.
  function handleSelectMonthInYear(month: number) {
    setMonthCursor({ year: miniYear, month });
    const node = bigCalRef.current;
    if (node !== null && typeof node.scrollIntoView === "function") {
      node.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }
  function handleSelectDay(isoDate: string) {
    void navigate({
      to: "/trades",
      search: {
        since: isoDate,
        until: isoDate,
        from: "calendar",
        cal_y: monthCursor.year,
        cal_m: monthCursor.month,
      },
    });
  }

  return (
    <section className="space-y-4 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-3xl font-medium tracking-tight text-on-surface">
          Calendar
        </h1>
        <label className="flex items-center gap-2 text-sm text-on-surface-variant">
          <span>P&amp;L</span>
          <select
            aria-label="P&L metric"
            data-testid="calendar-metric-select"
            value={metric}
            onChange={(event) => setMetric(event.target.value as PnlMetric)}
            className="h-9 rounded-control border border-outline bg-surface-container px-3 text-sm font-medium text-on-surface"
          >
            <option value="net">Net</option>
            <option value="gross">Gross</option>
          </select>
        </label>
      </header>

      <Card>
        <CardHeader>
          <div className="grid grid-cols-3 items-center gap-2">
            <div aria-hidden="true" />
            <div className="flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={handleYearBack}
                aria-label="Previous year"
                className="rounded-md border border-outline px-2 py-1 text-sm text-on-surface hover:bg-surface-container-high"
              >
                ‹
              </button>
              <span
                className="min-w-16 text-center text-base font-semibold text-on-surface"
                data-testid="calendar-mini-year"
              >
                {miniYear}
              </span>
              <button
                type="button"
                onClick={handleYearForward}
                aria-label="Next year"
                className="rounded-md border border-outline px-2 py-1 text-sm text-on-surface hover:bg-surface-container-high"
              >
                ›
              </button>
            </div>
            {/* Annual gross + net P&L for the overview's year — Gross on top,
                Net below — shown regardless of the page metric toggle. */}
            <div
              className="flex flex-col items-end text-xs leading-tight"
              data-testid="calendar-year-total"
            >
              <span>
                <span className="text-on-surface-muted">Gross </span>
                <span className={amountToneClass(miniYearSummary.grossPnl)}>
                  {formatMoney(miniYearSummary.grossPnl, { currency })}
                </span>
              </span>
              <span>
                <span className="text-on-surface-muted">Net </span>
                <span className={amountToneClass(miniYearSummary.netPnl)}>
                  {formatMoney(miniYearSummary.netPnl, { currency })}
                </span>
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {miniQuery.isLoading ? (
            <div
              className="mx-auto grid w-full max-w-2xl grid-cols-2 gap-2 sm:grid-cols-4"
              role="status"
              aria-live="polite"
            >
              <span className="sr-only">Loading calendar</span>
              {Array.from({ length: 12 }).map((_, index) => (
                <Skeleton key={index} className="h-24 w-full rounded-card" />
              ))}
            </div>
          ) : miniQuery.isError ? (
            <p className="py-8 text-center text-on-surface-variant">Failed to load calendar.</p>
          ) : (
            <div
              role="grid"
              aria-label="Year P&L overview"
              className="mx-auto grid w-full max-w-2xl grid-cols-2 gap-2 sm:grid-cols-4"
            >
              {Array.from({ length: 12 }, (_, month) => (
                <MiniMonthGrid
                  key={month}
                  monthIndex={month}
                  year={miniYear}
                  dayBuckets={miniDayBuckets}
                  metric={metric}
                  today={today}
                  isSelected={miniYear === monthCursor.year && month === monthCursor.month}
                  onSelect={handleSelectMonthInYear}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div ref={bigCalRef} className="mx-auto w-full max-w-4xl">
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleMonthBack}
                  aria-label="Previous month"
                  className="rounded-md border border-outline px-2 py-1 text-sm text-on-surface hover:bg-surface-container-high"
                >
                  ‹
                </button>
                <span className="min-w-32 text-center text-base font-semibold text-on-surface">
                  {monthLabel(monthCursor)}
                </span>
                <button
                  type="button"
                  onClick={handleMonthForward}
                  aria-label="Next month"
                  className="rounded-md border border-outline px-2 py-1 text-sm text-on-surface hover:bg-surface-container-high"
                >
                  ›
                </button>
                <button
                  type="button"
                  onClick={handleToday}
                  className="rounded-md border border-outline px-3 py-1 text-sm text-on-surface hover:bg-surface-container-high"
                >
                  Today
                </button>
              </div>
              <div className="flex flex-col items-end gap-0.5">
                <p
                  className="text-sm text-on-surface-variant"
                  data-testid="calendar-month-total"
                >
                  {metric === "gross" ? "Gross" : "Net"}{" "}
                  <span
                    className={`text-xl ${amountToneClass(
                      metric === "gross" ? monthSummary.grossPnl : monthSummary.netPnl,
                    )}`}
                  >
                    {formatMoney(
                      metric === "gross" ? monthSummary.grossPnl : monthSummary.netPnl,
                      { currency },
                    )}
                  </span>{" "}
                  · {monthSummary.tradeCount}{" "}
                  {monthSummary.tradeCount === 1 ? "trade" : "trades"}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {monthQuery.isLoading ? (
              <div role="status" aria-live="polite">
                <span className="sr-only">Loading calendar</span>
                <Skeleton className="h-80 w-full rounded-card" />
              </div>
            ) : monthQuery.isError ? (
              <p className="py-8 text-center text-danger" role="alert">
                {monthQuery.error instanceof Error
                  ? monthQuery.error.message
                  : "Failed to load calendar."}
              </p>
            ) : (
              <MonthGridTable
                grid={grid}
                dayBuckets={dayBuckets}
                weekBuckets={weekBuckets}
                currency={currency}
                metric={metric}
                today={today}
                onSelectDay={handleSelectDay}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
